import { Controller } from "@hotwired/stimulus"

// Enables browser push for review notifications.
//
// iOS is the constraint that shapes this: Safari only exposes Notification /
// PushManager to a site installed to the Home Screen (iOS 16.4+). In a normal
// Safari tab the APIs are simply absent, so we detect that case and show the
// install instructions instead of a button that would silently do nothing.
export default class extends Controller {
  static targets = ["enable", "status", "iosHelp", "unsupported"]
  static values = { vapidKey: String, url: String }

  connect() {
    this.render()
  }

  get isIOS() {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      // iPadOS 13+ reports as Mac; disambiguate by touch support.
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  }

  get isStandalone() {
    return window.navigator.standalone === true ||
      window.matchMedia("(display-mode: standalone)").matches
  }

  get supported() {
    return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window
  }

  render() {
    this.hide(this.enableTarget)
    this.hide(this.iosHelpTarget)
    this.hide(this.unsupportedTarget)

    if (!this.supported) {
      // On iPhone this is the expected state in a Safari tab — the fix is to
      // install to the Home Screen, not a different browser.
      this.isIOS && !this.isStandalone ? this.show(this.iosHelpTarget) : this.show(this.unsupportedTarget)
      return
    }

    if (Notification.permission === "denied") {
      this.status("Notifications are blocked in your browser settings for this site.")
      return
    }

    if (Notification.permission === "granted") {
      this.status("Notifications are on for this device.")
    }
    this.show(this.enableTarget)
  }

  async enable() {
    try {
      this.status("Requesting permission…")
      const permission = await Notification.requestPermission()
      if (permission !== "granted") {
        this.status("Permission denied — nothing will be sent.")
        return
      }

      this.status("Registering…")
      const registration = await navigator.serviceWorker.register("/service-worker.js")
      await navigator.serviceWorker.ready

      // Reuse an existing subscription if the browser already has one.
      let sub = await registration.pushManager.getSubscription()
      if (!sub) {
        sub = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: this.urlBase64ToUint8Array(this.vapidKeyValue),
        })
      }

      const json = sub.toJSON()
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "",
        },
        body: JSON.stringify({
          endpoint: sub.endpoint,
          p256dh: json.keys.p256dh,
          auth: json.keys.auth,
          user_agent: navigator.userAgent,
        }),
      })

      this.status(response.ok
        ? "Notifications are on for this device."
        : "Could not save the subscription — try again.")
    } catch (error) {
      this.status(`Could not enable notifications: ${error.message}`)
    }
  }

  status(text) {
    this.statusTarget.textContent = text
    this.show(this.statusTarget)
  }

  show(el) { el?.classList.remove("hidden") }
  hide(el) { el?.classList.add("hidden") }

  // VAPID keys travel as URL-safe base64; PushManager needs raw bytes.
  urlBase64ToUint8Array(base64String) {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4)
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/")
    const raw = window.atob(base64)
    return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)))
  }
}
